import { stringifyUrl } from 'query-string'
import { ReactElement, useMemo, useState } from 'react'

import { Button, ScreenReaderOnly, TypeStyleHeadlineMicro } from '@syconium/little-miss-figgy'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { NextLink } from '../../../../../app/_components/navigation/NextLink'
import {
  PricingRange,
  derivePricingForVariant,
} from '../../../../../app/_components/pricing/PricingRange.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../../../../brunswick/containers/cart'
import { useMakeCartItem } from '../../../../../brunswick/containers/cart/utils'

import {
  AddButtonWrapper,
  Controls,
  DetailText,
  Details,
  PriceAndColor,
  ProductName,
  SizeSelector,
  SizeSelectorOption,
  SizeSelectorWrapper,
  UpsellTile as StyledTile,
  Thumbnail,
  TileOverview,
} from './styles'
import { useUpsellsCarousel } from './UpsellsCarouselContext'

import type { UpsellTileVariantFragment } from '../../../../../__generated__/graphql/catalog/graphql'

gql(`
  fragment UpsellTileSelectedVariant on ProductCatalogVariant {
    id
    urlPath
    shopifyId
    shopifyProductId
    sku
    availability
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
  }
`)

gql(`
  fragment UpsellTileVariant on ProductCatalogVariant {
    ...UpsellTileSelectedVariant
    group {
      id
      title
      urlPath
      siteWidePromoEligible
      finalSale
      primaryCategory {
        id
        key
      }
      images(first: 1) {
        url
      }
      color {
        displayValue
      }
      variants {
        ...UpsellTileSelectedVariant
      }
      product {
        id
        handle
        options {
          sizes {
            size {
              id
              displayValue
            }
            outOfStock
          }
        }
      }
    }
  }
`)

export const UpsellTile = ({
  nostoPlacementId,
  variant: initialVariant,
  index,
}: {
  nostoPlacementId?: string
  variant: UpsellTileVariantFragment
  index: number
}): ReactElement => {
  const variantGroup = initialVariant.group
  const makeCartItem = useMakeCartItem()
  const { addItems } = CartContainer.useContainer()
  const { cart: miniCartCopy } = useTranslation()
  // Keyed by an incrementing id so adding the same product again re-announces
  // even though the message text is identical (a live region only speaks on a
  // DOM change).
  const [addAnnouncement, setAddAnnouncement] = useState({ announceId: 0, text: '' })

  const [selectedSizeDisplayString, setSelectedSizeDisplayString] = useState('')
  const sizeOptions = useMemo(() => {
    const sizes = variantGroup.product.options.sizes
    if (sizes.length === 0) return [{ value: 'O/S', label: 'O/S', outOfStock: false }]
    return sizes.map(({ size, outOfStock }) => ({
      value: size.displayValue,
      label: outOfStock ? `${size.displayValue} ${miniCartCopy.soldOutLabel}` : size.displayValue,
      outOfStock,
    }))
  }, [miniCartCopy.soldOutLabel, variantGroup.product.options.sizes])
  const selectedSizeKey = useMemo(() => {
    const sizes = variantGroup.product.options.sizes
    return (
      sizes.find(size => size.size.displayValue === selectedSizeDisplayString)?.size.id ??
      initialVariant.size?.id ??
      ''
    )
  }, [initialVariant.size?.id, selectedSizeDisplayString, variantGroup.product.options.sizes])

  const { currentSlide } = useUpsellsCarousel()

  const selectedVariant = useMemo(() => {
    return (
      variantGroup.variants.find(v => {
        return v.size?.id === selectedSizeKey && v.fit?.id === initialVariant.fit?.id
      }) ?? initialVariant
    )
  }, [initialVariant, selectedSizeKey, variantGroup.variants])

  const productHref = stringifyUrl({
    url: variantGroup.urlPath,
    query: { nosto: nostoPlacementId },
  })

  const pricing = useMemo(() => {
    return derivePricingForVariant(selectedVariant)
  }, [selectedVariant])

  const imageUrl = variantGroup.images?.[0]?.url
  const colorDisplayValue = variantGroup.color?.displayValue

  return (
    <StyledTile>
      <ScreenReaderOnly
        as='span'
        role='status'
        aria-live='assertive'
        aria-atomic='true'
        key={addAnnouncement.announceId}
      >
        {addAnnouncement.text}
      </ScreenReaderOnly>
      <NextLink href={productHref} tabIndex={currentSlide === index ? 0 : -1}>
        <TileOverview>
          {imageUrl ? <Thumbnail imgUrl={imageUrl} /> : null}
          <Details>
            <ProductName>{variantGroup.title}</ProductName>
            <PriceAndColor>
              {pricing ? (
                <TypeStyleHeadlineMicro>
                  <PricingRange {...pricing} />
                </TypeStyleHeadlineMicro>
              ) : null}
              {colorDisplayValue ? <DetailText>{colorDisplayValue}</DetailText> : null}
            </PriceAndColor>
          </Details>
        </TileOverview>
      </NextLink>
      <Controls>
        {Boolean(sizeOptions.length) && (
          <SizeSelectorWrapper data-testid='FIGS__Upsells__SizeSelectorWrapper'>
            <SizeSelector
              value={selectedSizeDisplayString}
              onChange={setSelectedSizeDisplayString}
              heading={miniCartCopy.selectSize}
              isInvalid={false}
              placement='bottom-start'
              // Portal out of the swiper, whose `transform` would otherwise trap the popover
              // below the sticky checkout footer. Flipping stays on: prefer opening down (so the
              // product image isn't covered), but flip up when down would run off-viewport.
              portal
              renderSelectValue={value => value || miniCartCopy.selectSize}
              tabIndex={currentSlide === index ? 0 : -1}
              data-testid='FIGS__Upsells__SizeSelector'
            >
              {sizeOptions.map(({ value, label, outOfStock }) => (
                <SizeSelectorOption key={value} value={value} disabled={outOfStock}>
                  {label}
                </SizeSelectorOption>
              ))}
            </SizeSelector>
          </SizeSelectorWrapper>
        )}

        <AddButtonWrapper>
          <Button
            allCaps
            block
            data-testid='FIGS__Upsells__AddButton'
            disabled={selectedVariant.availability !== 'AVAILABLE'}
            onClick={() => {
              addItems(
                makeCartItem({
                  product: variantGroup.product,
                  variant: selectedVariant,
                  variantGroup,
                  options: {
                    attributedPortal: undefined,
                  },
                })
              )
              const addedText = miniCartCopy.upsell.accessibility.addedToBag.replace(
                '{{product}}',
                variantGroup.title
              )
              requestAnimationFrame(() => {
                setAddAnnouncement(prev => ({ announceId: prev.announceId + 1, text: addedText }))
              })
            }}
            size='large'
            tabIndex={currentSlide === index ? 0 : -1}
          >
            {miniCartCopy.add}
          </Button>
        </AddButtonWrapper>
      </Controls>
    </StyledTile>
  )
}
